import { useState, useCallback, useRef, useEffect } from "react";
import { useConversation } from "@elevenlabs/react";
import { useAuthFetch } from "./useAuthFetch";
import type { VoiceState, VoiceMessage, SIPOCData, QuestionType } from "@acde/shared";

interface VoiceInterviewQuestion {
  text: string;
  id?: string;
  type?: QuestionType;
  options?: string[];
}

interface VoiceAttachment {
  id?: string;
  name: string;
  type: string;
  analysis?: string;
  uploadedAt?: string;
}

interface UseVoiceConversationOptions {
  sessionId?: string;
  previousMessages?: VoiceMessage[];
  processName?: string;
  sipocData?: SIPOCData;
  currentPhase?: string;
  completionPercentage?: number;
  currentQuestion?: string;
  currentQuestionIndex?: number;
  totalQuestions?: number;
  allQuestions?: VoiceInterviewQuestion[];
  attachments?: VoiceAttachment[];
}

export interface VoiceConversationState {
  isConnected: boolean;
  isConnecting: boolean;
  voiceState: VoiceState;
  messages: VoiceMessage[];
  currentTranscript: string;
  isUserSpeaking: boolean;
  error: string | null;
  isMuted: boolean;
}

export interface VoiceConversationActions {
  startConversation: () => Promise<void>;
  endConversation: () => Promise<void>;
  toggleMute: () => void;
  retryConnection: () => void;
}

export function useVoiceConversation(
  options: UseVoiceConversationOptions = {}
): VoiceConversationState & VoiceConversationActions {
  const {
    sessionId,
    previousMessages = [],
    processName,
    sipocData,
    currentPhase,
    completionPercentage,
    currentQuestion,
    currentQuestionIndex,
    totalQuestions,
    allQuestions = [],
    attachments = [],
  } = options;
  const { authFetch } = useAuthFetch();
  const [isConnecting, setIsConnecting] = useState(false);
  // Initialize messages with previousMessages to fix first-load display
  const [messages, setMessages] = useState<VoiceMessage[]>(
    () => previousMessages
  );
  const [currentTranscript, setCurrentTranscript] = useState("");
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [voiceState, setVoiceState] = useState<VoiceState>("idle");
  const [isConnected, setIsConnected] = useState(false);

  // Determine if this is truly a continuation (user has actually participated)
  // Only count as continuation if there's at least one meaningful user message
  const hasUserParticipated = previousMessages.some(
    (m) =>
      m.role === "user" &&
      m.content?.trim() &&
      m.content.trim() !== "..." &&
      m.content.trim().length > 0
  );

  // Track if we have conversation history to continue from
  // Only true if user has actually participated, not just received a greeting
  const hasConversationHistory =
    previousMessages.length > 0 && hasUserParticipated;

  // Sync messages with previousMessages when they change (for loading saved transcripts)
  const previousMessagesLengthRef = useRef(previousMessages.length);
  useEffect(() => {
    // Only sync if previousMessages changed and we're not in an active conversation
    // This handles cases where previousMessages loads after initial render
    if (
      previousMessages.length > 0 &&
      previousMessages.length !== previousMessagesLengthRef.current &&
      !isConnected
    ) {
      console.log(
        "[useVoiceConversation] Syncing previousMessages:",
        previousMessages.length
      );
      setMessages(previousMessages);
      previousMessagesLengthRef.current = previousMessages.length;
    }
  }, [previousMessages, isConnected]);

  const currentUserMessageRef = useRef<string>("");
  const currentAssistantMessageRef = useRef<string>("");
  const lastAddedMessageRef = useRef<{ role: string; content: string } | null>(
    null
  );
  const isStartingRef = useRef(false);
  const authFetchRef = useRef(authFetch);
  const voiceStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const lastVoiceStateRef = useRef<VoiceState>("idle");
  const userSpeechTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Keep authFetch ref updated
  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  // Debounced voice state setter to prevent flickering
  const setStableVoiceState = useCallback((newState: VoiceState) => {
    // Clear any pending state change
    if (voiceStateTimeoutRef.current) {
      clearTimeout(voiceStateTimeoutRef.current);
      voiceStateTimeoutRef.current = null;
    }

    // Immediate transitions for speaking (important to show)
    // Debounce transitions from speaking to listening (prevents flicker)
    if (
      newState === "speaking" ||
      newState === "idle" ||
      newState === "connecting"
    ) {
      lastVoiceStateRef.current = newState;
      setVoiceState(newState);
    } else if (
      newState === "listening" &&
      lastVoiceStateRef.current === "speaking"
    ) {
      // Debounce speaking -> listening transition to prevent flicker
      voiceStateTimeoutRef.current = setTimeout(() => {
        lastVoiceStateRef.current = newState;
        setVoiceState(newState);
        voiceStateTimeoutRef.current = null;
      }, 150);
    } else {
      lastVoiceStateRef.current = newState;
      setVoiceState(newState);
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (voiceStateTimeoutRef.current) {
        clearTimeout(voiceStateTimeoutRef.current);
      }
      if (userSpeechTimeoutRef.current) {
        clearTimeout(userSpeechTimeoutRef.current);
      }
    };
  }, []);

  const addMessage = useCallback(
    (
      role: "user" | "assistant",
      content: string,
      isFinal: boolean,
      clearTranscript: boolean = false
    ) => {
      const trimmedContent = content.trim();
      if (!trimmedContent) return;

      // Prevent duplicate messages - check if this exact message was just added
      if (
        lastAddedMessageRef.current?.role === role &&
        lastAddedMessageRef.current?.content === trimmedContent
      ) {
        return;
      }

      lastAddedMessageRef.current = { role, content: trimmedContent };

      setMessages((prev) => {
        // Also check if this exact content already exists in recent messages (last 5)
        // This prevents duplicates when mode changes or events fire multiple times
        const recentMessages = prev.slice(-5);
        const isDuplicate = recentMessages.some(
          (m) => m.role === role && m.content === trimmedContent
        );

        if (isDuplicate) {
          console.log(
            "[Voice] Prevented duplicate message:",
            trimmedContent.substring(0, 50)
          );
          return prev;
        }

        return [
          ...prev,
          {
            id: crypto.randomUUID(),
            role,
            content: trimmedContent,
            timestamp: new Date(),
            isFinal,
          },
        ];
      });

      // Only clear transcript when explicitly requested (e.g., on mode change)
      if (clearTranscript) {
        setCurrentTranscript("");
      }
    },
    []
  );

  const conversation = useConversation({
    micMuted: isMuted,
    onConnect: () => {
      setIsConnecting(false);
      setIsConnected(true);
      setStableVoiceState("listening");
      setError(null);
      isStartingRef.current = false;
    },
    onDisconnect: () => {
      setIsConnected(false);
      setStableVoiceState("idle");
      isStartingRef.current = false;

      if (currentUserMessageRef.current) {
        addMessage("user", currentUserMessageRef.current, true, true);
        currentUserMessageRef.current = "";
      }
      if (currentAssistantMessageRef.current) {
        addMessage("assistant", currentAssistantMessageRef.current, true, true);
        currentAssistantMessageRef.current = "";
      }
    },
    onMessage: (message) => {
      const msg = message as {
        type?: string;
        source?: string;
        role?: string;
        message?: string;
        transcript?: string;
        is_final?: boolean;
        isFinal?: boolean;
        final?: boolean;
      };

      const msgContent = msg.message || msg.transcript || "";
      // Check multiple possible final flags
      const isFinal =
        msg.is_final === true || msg.isFinal === true || msg.final === true;

      const isAgent =
        msg.type === "agent_response" ||
        msg.type === "agent_response_correction" ||
        msg.source === "ai" ||
        msg.role === "agent";
      const isUser =
        msg.type === "user_transcript" ||
        msg.source === "user" ||
        msg.role === "user";

      if (isUser && msgContent) {
        currentUserMessageRef.current = msgContent;
        setCurrentTranscript(msgContent);
        setIsUserSpeaking(true);

        // Clear any pending speech timeout
        if (userSpeechTimeoutRef.current) {
          clearTimeout(userSpeechTimeoutRef.current);
        }

        // If this is a final transcript, add it immediately
        if (isFinal && msgContent.trim().length > 2) {
          addMessage("user", msgContent, true);
          currentUserMessageRef.current = ""; // Clear to prevent duplicate on mode change
        } else if (msgContent.trim().length > 2) {
          // Set a debounced timeout to capture the message if no more speech comes
          // This handles cases where ElevenLabs doesn't send a final flag
          userSpeechTimeoutRef.current = setTimeout(() => {
            if (
              currentUserMessageRef.current &&
              currentUserMessageRef.current.trim().length > 2
            ) {
              console.log(
                "[Voice] Speech timeout - capturing user message:",
                currentUserMessageRef.current.substring(0, 50)
              );
              addMessage("user", currentUserMessageRef.current, true);
              currentUserMessageRef.current = "";
            }
          }, 1500); // Wait 1.5 seconds of silence before capturing
        }
      } else if (isAgent && msgContent) {
        // Clear any pending speech timeout since AI is responding
        if (userSpeechTimeoutRef.current) {
          clearTimeout(userSpeechTimeoutRef.current);
          userSpeechTimeoutRef.current = null;
        }

        // IMPORTANT: Before processing agent response, capture any pending user message
        // This ensures we don't lose the user's speech when the AI starts responding
        if (
          currentUserMessageRef.current &&
          currentUserMessageRef.current.trim().length > 2
        ) {
          console.log(
            "[Voice] Agent response - capturing pending user message:",
            currentUserMessageRef.current.substring(0, 50)
          );
          addMessage("user", currentUserMessageRef.current, true);
          currentUserMessageRef.current = "";
        }

        currentAssistantMessageRef.current = msgContent;
        setCurrentTranscript(msgContent);
        setIsUserSpeaking(false);
        addMessage("assistant", msgContent, true);
        currentAssistantMessageRef.current = "";
      }
    },
    onError: (err) => {
      let errorMessage = "Voice connection error";
      if (typeof err === "string") {
        errorMessage = err;
      } else if (err && typeof err === "object") {
        errorMessage =
          (err as { message?: string }).message || JSON.stringify(err);
      }
      setError(errorMessage);
      setIsConnecting(false);
      setIsConnected(false);
      setStableVoiceState("idle");
      isStartingRef.current = false;
    },
    onModeChange: (mode) => {
      if (mode.mode === "speaking") {
        // Clear any pending speech timeout since mode is changing
        if (userSpeechTimeoutRef.current) {
          clearTimeout(userSpeechTimeoutRef.current);
          userSpeechTimeoutRef.current = null;
        }

        // Capture any pending user message before AI starts speaking
        if (
          currentUserMessageRef.current &&
          currentUserMessageRef.current.trim().length > 2
        ) {
          console.log(
            "[Voice] Mode change to speaking - capturing user message:",
            currentUserMessageRef.current.substring(0, 50)
          );
          addMessage("user", currentUserMessageRef.current, true, true);
          currentUserMessageRef.current = "";
        } else {
          setCurrentTranscript("");
        }
        setStableVoiceState("speaking");
        setIsUserSpeaking(false);
      } else if (mode.mode === "listening") {
        setStableVoiceState("listening");
        // Don't clear transcript here - let it persist until new content arrives
      }
    },
    onStatusChange: (status) => {
      if (status.status === "connected") {
        setIsConnected(true);
        setIsConnecting(false);
        setStableVoiceState("listening");
      } else if (status.status === "disconnected") {
        setIsConnected(false);
        setIsConnecting(false);
        setStableVoiceState("idle");
      } else if (status.status === "connecting") {
        setIsConnecting(true);
        setStableVoiceState("connecting");
      }
    },
    useWakeLock: true,
  });

  const requestMicrophonePermission =
    useCallback(async (): Promise<boolean> => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach((track) => track.stop());
        return true;
      } catch (err) {
        if (err instanceof Error) {
          if (err.name === "NotAllowedError") {
            setError(
              "Microphone access denied. Please allow microphone access and try again."
            );
          } else if (err.name === "NotFoundError") {
            setError(
              "No microphone found. Please connect a microphone and try again."
            );
          } else {
            setError(`Microphone error: ${err.message}`);
          }
        }
        return false;
      }
    }, []);

  // Build conversation context summary for continuing sessions
  // Uses ElevenLabs agent override pattern for session continuity
  const buildConversationContext = useCallback(() => {
    const parts: string[] = [];
    const normalizeOptions = (options?: string[]) =>
      (options || []).map((option) => option.trim()).filter(Boolean);
    const summarizeAttachments = (items: VoiceAttachment[]) => {
      if (items.length === 0) return [];
      const recentItems = items.slice(-3);
      return recentItems.map((item) => {
        const analysisSnippet = item.analysis
          ? item.analysis.replace(/\s+/g, " ").trim().slice(0, 280)
          : "";
        return `- ${item.name} (${item.type})${
          analysisSnippet ? `: ${analysisSnippet}` : ""
        }`;
      });
    };

    const currentQuestionDetails =
      currentQuestionIndex !== undefined
        ? allQuestions[currentQuestionIndex]
        : undefined;
    const currentQuestionText =
      currentQuestionDetails?.text || currentQuestion || "";
    const currentQuestionOptions =
      currentQuestionDetails?.type === "multiple_choice"
        ? normalizeOptions(currentQuestionDetails.options)
        : currentQuestionDetails?.type === "rating"
          ? ["1", "2", "3", "4", "5"]
          : currentQuestionDetails?.type === "yes_no"
            ? ["Yes", "No"]
            : [];

    // CRITICAL: Include interview question context for proper continuation
    if (currentQuestionText && totalQuestions) {
      parts.push(`## CURRENT INTERVIEW STATE
You are conducting question ${
        (currentQuestionIndex ?? 0) + 1
      } of ${totalQuestions}.
Current Question: "${currentQuestionText}"
Progress: ${currentQuestionIndex ?? 0} questions completed.`);
      if (currentQuestionOptions.length > 0) {
        parts.push(`Answer options: ${currentQuestionOptions.join(", ")}`);
      }
    }

    // Include full question list so AI knows what questions to ask
    if (allQuestions.length > 0) {
      const questionList = allQuestions
        .map((q, i) => {
          const isCurrent = i === (currentQuestionIndex ?? 0);
          const isCompleted = i < (currentQuestionIndex ?? 0);
          const status = isCurrent
            ? " (CURRENT)"
            : isCompleted
            ? " (COMPLETED)"
            : "";
          const options =
            q.type === "multiple_choice"
              ? normalizeOptions(q.options)
              : q.type === "rating"
                ? ["1", "2", "3", "4", "5"]
                : q.type === "yes_no"
                  ? ["Yes", "No"]
                  : [];
          const optionsSuffix =
            options.length > 0 ? ` (Options: ${options.join(", ")})` : "";
          return `${i + 1}. ${q.text}${optionsSuffix}${status}`;
        })
        .join("\n");

      parts.push(`## FULL INTERVIEW QUESTIONS
${questionList}

After the user answers the current question satisfactorily, move to the next unanswered question in the list.`);
    }

    if (attachments.length > 0) {
      const attachmentLines = summarizeAttachments(attachments);
      if (attachmentLines.length > 0) {
        parts.push(`## SHARED FILES\n${attachmentLines.join("\n")}`);
      }
    }

    // Add process info if available
    if (processName) {
      parts.push(`## Process Being Documented: ${processName}`);
    }

    // Add phase and completion info
    if (currentPhase) {
      parts.push(`## Current Phase: ${currentPhase}`);
    }
    if (completionPercentage !== undefined) {
      parts.push(`## Completion: ${completionPercentage}%`);
    }

    // Add SIPOC summary if available
    if (sipocData) {
      const sipocSummary: string[] = [];
      if (sipocData.suppliers?.length) {
        sipocSummary.push(
          `Suppliers identified: ${sipocData.suppliers
            .map((s) => s.entity)
            .join(", ")}`
        );
      }
      if (sipocData.inputs?.length) {
        sipocSummary.push(
          `Inputs identified: ${sipocData.inputs.map((i) => i.item).join(", ")}`
        );
      }
      if (sipocData.outputs?.length) {
        sipocSummary.push(
          `Outputs identified: ${sipocData.outputs
            .map((o) => o.item)
            .join(", ")}`
        );
      }
      if (sipocData.customers?.length) {
        sipocSummary.push(
          `Customers identified: ${sipocData.customers
            .map((c) => c.entity)
            .join(", ")}`
        );
      }
      if (sipocSummary.length > 0) {
        parts.push(
          `## SIPOC Elements Already Discovered\n${sipocSummary.join("\n")}`
        );
      }
    }

    // Get last few exchanges (up to 10 messages) for context - only if user has participated
    if (
      hasConversationHistory &&
      hasUserParticipated &&
      previousMessages.length > 0
    ) {
      const recentMessages = previousMessages.slice(-10);
      const contextSummary = recentMessages
        .map((m) => `${m.role === "user" ? "User" : "Assistant"}: ${m.content}`)
        .join("\n");

      parts.push(`## Recent Conversation History\n${contextSummary}`);
    }

    // Instructions depend on whether this is a true continuation or fresh start
    if (hasConversationHistory && hasUserParticipated) {
      // True continuation - user has participated
      parts.push(`## IMPORTANT INSTRUCTIONS FOR VOICE MODE CONTINUATION
1. You are CONTINUING an existing conversation, NOT starting fresh.
2. The user just switched to voice mode from the chat interface.
3. ${
        currentQuestion
          ? `Continue with the current question: "${currentQuestion}"`
          : "Continue from where the conversation left off."
      }
4. Do NOT greet the user again or introduce yourself - they already know you.
5. Do NOT repeat questions that have already been answered.
6. If the user was mid-answer, prompt them to continue their thought.
7. Acknowledge you're resuming briefly: "Let's continue where we left off" or similar.`);
    } else {
      // Fresh start - user hasn't participated yet or this is a new session
      parts.push(`## INSTRUCTIONS FOR NEW VOICE SESSION
1. This is a fresh start - the user is beginning the voice interview.
2. ${
        currentQuestion
          ? `Start with the current question: "${currentQuestion}"`
          : "Welcome the user and begin the interview."
      }
3. Introduce yourself briefly and naturally.
4. Be warm and conversational.`);
    }

    parts.push(`## STRUCTURED QUESTION TYPES
If a question is multiple_choice:
- Read the options aloud as part of the question.
- Ask the user to choose one of the options.
- Allow them to provide a different answer if they prefer.
If a question is rating:
- Ask the user to rate on a scale of 1 to 5.
- Accept their numerical answer and move on.
If a question is yes_no:
- Present it as a yes/no question.
- Accept their answer and move on.`);

    // Add interview flow instructions for all sessions
    parts.push(`## INTERVIEW CONVERSATION FLOW
1. ALWAYS guide the conversation - never leave the user wondering what to say next.
2. After the user answers a question:
   - Briefly acknowledge their response (e.g., "That's interesting" or "Thank you for sharing that")
   - Either ask a clarifying follow-up if their answer was brief, OR
   - Move to the next question
3. If the user gives a detailed answer, thank them and proceed to the next question.
4. If the user gives a brief answer (just a few words), ask them to elaborate before moving on.
5. Keep the conversation flowing naturally - don't wait silently for the user to speak.
6. When you've covered all questions, let the user know they've completed the interview and can submit.
7. Be encouraging and make the user feel comfortable sharing.

## IMPORTANT: QUESTION TRANSITION FORMAT
When moving to a new question, you MUST use this exact format:
"Now, moving to question [NUMBER]: [question text]"

Examples:
- "Great insight! Now, moving to question 2: How often does this happen?"
- "Thank you for that detail. Now, moving to question 3: What tools do you currently use?"

This format is required for the progress indicator to work correctly. Always include the question number when transitioning.

## DETECTING WHEN USER WANTS TO MOVE ON
If the user says any of these phrases, IMMEDIATELY move to the next question:
- "that's all", "thats all", "that is all"
- "nothing else", "nothing more"
- "done", "I'm done", "finished", "finish"
- "next", "next question", "move on"
- "let's continue", "continue"
- "skip", "pass"

Do NOT keep asking them to elaborate if they indicate they want to move on. Instead, acknowledge and proceed:
"Understood. Now, moving to question [NUMBER]: [next question text]"

If they are on the last question and want to finish, say:
"That completes all our questions. You can now submit your interview when ready."`);

    return parts.join("\n\n");
  }, [
    hasConversationHistory,
    hasUserParticipated,
    previousMessages,
    processName,
    sipocData,
    currentPhase,
    completionPercentage,
    currentQuestion,
    currentQuestionIndex,
    totalQuestions,
    allQuestions,
    attachments,
  ]);

  const startConversation = useCallback(async () => {
    if (isStartingRef.current || isConnecting) {
      return;
    }

    try {
      isStartingRef.current = true;
      setIsConnecting(true);
      setStableVoiceState("connecting");
      setError(null);

      // Only clear messages if starting fresh (no previous messages)
      if (!hasConversationHistory) {
        setMessages([]);
      }

      const hasMicPermission = await requestMicrophonePermission();
      if (!hasMicPermission) {
        setIsConnecting(false);
        setStableVoiceState("idle");
        isStartingRef.current = false;
        return;
      }

      // Get signed URL from API with authentication
      const apiUrl = import.meta.env.VITE_API_URL || "/api";
      const continueParam = hasConversationHistory ? "&continue=true" : "";

      const response = await authFetchRef.current(
        `${apiUrl}/voice/signed-url?sessionId=${
          sessionId || ""
        }${continueParam}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to get voice connection");
      }

      const data = await response.json();
      const { signedUrl, hasContext } = data;

      if (!signedUrl) {
        throw new Error("No signed URL returned from server");
      }

      if (hasContext) {
        console.log(
          "[Voice] Starting session with conversation context from server"
        );
      }

      // Build client-side overrides for continuing conversations
      // Per ElevenLabs docs: use overrides.agent.prompt.prompt for context
      // and overrides.agent.firstMessage for the initial greeting
      const buildFirstMessage = () => {
        const normalizeOptions = (options?: string[]) =>
          (options || []).map((option) => option.trim()).filter(Boolean);
        const formatQuestionForPrompt = (
          questionDetails?: VoiceInterviewQuestion,
          fallbackText?: string
        ) => {
          const baseText = questionDetails?.text?.trim() || fallbackText?.trim();
          if (!baseText) return "";
          if (questionDetails?.type === "multiple_choice") {
            const options = normalizeOptions(questionDetails.options);
            if (options.length > 0) {
              return `${baseText} Options are: ${options.join(", ")}.`;
            }
          }
          if (questionDetails?.type === "rating") {
            return `${baseText} Please rate on a scale of 1 to 5.`;
          }
          if (questionDetails?.type === "yes_no") {
            return `${baseText} Please answer Yes or No.`;
          }
          return baseText;
        };
        const attachmentSummary = attachments.length
          ? attachments
              .slice(-2)
              .map((item) => item.name)
              .filter(Boolean)
              .join(" and ")
          : "";
        const attachmentPrefix = attachmentSummary
          ? `I see you've shared ${attachmentSummary}. `
          : "";

        const currentQuestionDetails =
          currentQuestionIndex !== undefined
            ? allQuestions[currentQuestionIndex]
            : undefined;
        const currentQuestionText = formatQuestionForPrompt(
          currentQuestionDetails,
          currentQuestion
        );

        // Get the last few messages to understand context
        const lastUserMessage = previousMessages
          .filter(
            (m) =>
              m.role === "user" &&
              m.content?.trim() &&
              m.content.trim() !== "..."
          )
          .pop();
        const lastAssistantMessage = previousMessages
          .filter((m) => m.role === "assistant")
          .pop();

        // Check if user just provided an answer (last message is from user)
        const userJustAnswered =
          lastUserMessage &&
          previousMessages.indexOf(lastUserMessage) >
            previousMessages.indexOf(
              lastAssistantMessage || previousMessages[0]
            );

        // If user has participated in the conversation, this is a true continuation
        if (hasConversationHistory && hasUserParticipated) {
          // If user just answered something in chat, acknowledge it and continue
          if (userJustAnswered && lastUserMessage) {
            const userAnswer = lastUserMessage.content.trim();
            const shortAnswer =
              userAnswer.length <= 50
                ? userAnswer
                : userAnswer.substring(0, 47) + "...";

            // If the answer is very brief, ask for elaboration
            if (userAnswer.length < 20) {
              return `I see you mentioned "${shortAnswer}". Could you tell me a bit more about that?`;
            }

            // For longer answers, acknowledge and potentially move to next question
            if (
              currentQuestionIndex !== undefined &&
              totalQuestions &&
              currentQuestionIndex < totalQuestions - 1
            ) {
              const nextQuestionNum = currentQuestionIndex + 2; // +1 for 0-index, +1 for next
              const nextQuestionDetails =
                allQuestions[currentQuestionIndex + 1];
              const nextQuestionText =
                formatQuestionForPrompt(nextQuestionDetails);
              if (nextQuestionText) {
                return `Thanks for sharing that. Now, moving to question ${nextQuestionNum}: ${nextQuestionText}`;
              }
            }

            return `Thanks for sharing that. Let me continue from where we left off.`;
          }

          // User participated but last message was from assistant - just continue
          if (currentQuestionText) {
            const questionNum = totalQuestions
              ? `Question ${
                  (currentQuestionIndex ?? 0) + 1
                } of ${totalQuestions}`
              : "";
            return `Let's continue where we left off. ${
              questionNum ? `We're on ${questionNum.toLowerCase()}. ` : ""
            }${attachmentPrefix}${currentQuestionText}`;
          }

          return "Great, let's continue where we left off.";
        }

        // User hasn't participated yet - this is effectively a new conversation
        if (currentQuestionText && totalQuestions) {
          const questionNum = (currentQuestionIndex ?? 0) + 1;
          return `Hi! I'll be guiding you through ${totalQuestions} questions today. ${attachmentPrefix}Let's start with question ${questionNum}: ${currentQuestionText}`;
        }

        return undefined;
      };

      const contextPrompt = buildConversationContext();
      const firstMessage = buildFirstMessage();

      // Only apply overrides if we have context to pass
      const sessionOverrides =
        contextPrompt || firstMessage
          ? {
              agent: {
                ...(firstMessage && { firstMessage }),
                ...(contextPrompt && {
                  prompt: {
                    prompt: contextPrompt,
                  },
                }),
              },
            }
          : undefined;

      if (sessionOverrides) {
        console.log("[Voice] Starting with session overrides:", {
          hasFirstMessage: !!firstMessage,
          hasPrompt: !!contextPrompt,
          currentQuestion: currentQuestion?.substring(0, 50),
          questionIndex: currentQuestionIndex,
        });
      }

      // Use WebSocket connection with signed URL and optional overrides
      await conversation.startSession({
        signedUrl,
        connectionType: "websocket",
        overrides: sessionOverrides,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start voice mode"
      );
      setIsConnecting(false);
      setStableVoiceState("idle");
      isStartingRef.current = false;
    }
  }, [
    conversation,
    isConnecting,
    requestMicrophonePermission,
    sessionId,
    setStableVoiceState,
    hasConversationHistory,
    hasUserParticipated,
    buildConversationContext,
    currentQuestion,
    currentQuestionIndex,
    totalQuestions,
    allQuestions,
    previousMessages,
    attachments,
  ]);

  const endConversation = useCallback(async () => {
    try {
      await conversation.endSession();
    } catch {
      // Silently handle end session errors
    }
    setStableVoiceState("idle");
    setIsConnecting(false);
    setIsConnected(false);
    isStartingRef.current = false;
  }, [conversation, setStableVoiceState]);

  const retryConnection = useCallback(() => {
    setError(null);
    isStartingRef.current = false;
    startConversation();
  }, [startConversation]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  useEffect(() => {
    return () => {
      if (conversationRef.current.status === "connected") {
        conversationRef.current.endSession();
      }
    };
  }, []);

  return {
    isConnected,
    isConnecting,
    voiceState,
    messages,
    currentTranscript,
    isUserSpeaking,
    error,
    isMuted,
    startConversation,
    endConversation,
    toggleMute,
    retryConnection,
  };
}
